import {
  Wallet,
  Eye,
  Star,
  TrendingUp,
  Shield,
  MessageCircle,
} from "lucide-react";
import Button from "../components/Button";
import { type ReactNode } from "react";

interface FeatureItemProps {
  icon: ReactNode;
  title: string;
  description: string;
}

function FeatureCardSmall({ icon, title, description }: FeatureItemProps) {
  return (
    <div className="rounded-xl bg-white/[0.03] border border-surface-border/30 p-5 hover:bg-white/[0.06] transition-all duration-300 group h-full">
      <div className="flex items-start gap-3">
        <div className="flex-shrink-0 w-9 h-9 rounded-lg bg-accent-pink/10 flex items-center justify-center text-accent-pink group-hover:bg-accent-pink/20 transition-colors duration-300">
          {icon}
        </div>
        <div>
          <h4 className="text-sm font-semibold text-white mb-1">{title}</h4>
          <p className="text-xs text-gray-400 leading-relaxed">{description}</p>
        </div>
      </div>
    </div>
  );
}

function FeatureCardTall({ icon, title, description }: FeatureItemProps) {
  return (
    <div className="rounded-xl bg-white/[0.03] border border-surface-border/30 p-6 hover:bg-white/[0.06] transition-all duration-300 group h-full flex flex-col justify-center">
      <div className="flex items-start gap-4">
        <div className="flex-shrink-0 w-11 h-11 rounded-lg bg-accent-pink/10 flex items-center justify-center text-accent-pink group-hover:bg-accent-pink/20 transition-colors duration-300">
          {icon}
        </div>
        <div>
          <h4 className="text-base font-semibold text-white mb-2">{title}</h4>
          <p className="text-sm text-gray-400 leading-relaxed">{description}</p>
        </div>
      </div>
    </div>
  );
}

const features: FeatureItemProps[] = [
  {
    icon: <Wallet size={20} />,
    title: "Guaranteed Crypto Payments",
    description:
      "Funds are escrowed before you start. Get paid in crypto the moment work is approved.",
  },
  {
    icon: <Eye size={20} />,
    title: "Stay Anonymous",
    description:
      "Work under a pseudonym. Your real identity is never shared until you choose to reveal it.",
  },
  {
    icon: <Star size={20} />,
    title: "Build Your Reputation",
    description:
      "Earn ratings and reviews that follow your anonymous profile. Stand out without exposure.",
  },
  {
    icon: <TrendingUp size={20} />,
    title: "Career Growth",
    description:
      "Level up from entry-level to premium contracts. Your track record unlocks higher-paying gigs.",
  },
  {
    icon: <Shield size={20} />,
    title: "Dispute Protection",
    description:
      "If something goes wrong, our dispute system ensures fair resolution with escrowed funds.",
  },
  {
    icon: <MessageCircle size={20} />,
    title: "Secure In-App Chat",
    description:
      "Communicate directly with agencies through our encrypted, anti-bypass messaging system.",
  },
];

export default function ForWorkers() {
  return (
    <section
      id="for-workers"
      className="relative bg-[#0F0A28] noise-overlay"
    >
      {/* Subtle gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-bl from-accent-pink/5 via-transparent to-transparent pointer-events-none" />

      <div className="section-padding relative">
        <div className="section-container">
          {/* Header — right aligned with right accent line */}
          <div className="mb-16 lg:text-right">
            <span className="text-accent-pink uppercase tracking-widest text-xs font-mono mb-4 block">
              For Workers
            </span>
            <div className="lg:ml-auto max-w-3xl border-l-[3px] border-l-accent-pink pl-4 lg:border-l-0 lg:pl-0 lg:border-r-[3px] lg:border-r-accent-pink lg:pr-4">
              <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-4 leading-[1.1]">
                Get Paid Guaranteed{" "}
                <span className="text-gradient">via Escrow</span>
              </h2>
              <p className="text-lg text-gray-400 leading-relaxed">
                No more chasing payments. With crypto escrow, your earnings are
                secured before you start working. Focus on delivering, not
                worrying about getting paid.
              </p>
            </div>
          </div>

          {/* Asymmetric grid: 2 tall cards left, 4 small cards stacked right */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 mb-12">
            {/* Left column: 2 tall cards */}
            <div className="lg:col-span-5 flex flex-col gap-4">
              <div className="flex-1">
                <FeatureCardTall {...features[0]!} />
              </div>
              <div className="flex-1">
                <FeatureCardTall {...features[1]!} />
              </div>
            </div>

            {/* Right column: 4 smaller cards in 2x2 */}
            <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-4">
              <FeatureCardSmall {...features[2]!} />
              <FeatureCardSmall {...features[3]!} />
              <FeatureCardSmall {...features[4]!} />
              <FeatureCardSmall {...features[5]!} />
            </div>
          </div>

          {/* CTA — right aligned on desktop to match header */}
          <div className="lg:text-right">
            <Button
              variant="gradient"
              size="lg"
              href="#cta"
              className="!bg-gradient-purple"
            >
              Find Work
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
}
